import { call, put, all, fork, takeEvery } from 'redux-saga/effects'

import { showLoading, hideLoading } from 'react-redux-loading-bar'

import ConfigurationsService from '../middlewares/configurations'

import { notify } from 'reapop'
import i18n from 'i18next'

import {
  GET_CONFIGURATIONS_REQUEST,
  GET_CONFIGURATIONS,
  GET_CONFIGURATION_REQUEST,
  GET_CONFIGURATION,
  UPDATE_CONFIGURATION_REQUEST,
  UPDATE_CONFIGURATION,
  CREATE_CONFIGURATION_REQUEST,
  CREATE_CONFIGURATION,
  DELETE_CONFIGURATION_REQUEST,
  DELETE_CONFIGURATION,
  TOGGLE_CONFIGURATION_STATUS_REQUEST,
  TOGGLE_CONFIGURATION_STATUS,
  GET_CONFIGURATIONS_REQUEST_PENDING,
  GET_CONFIGURATIONS_REQUEST_SUCCESS,
  GET_CONFIGURATIONS_REQUEST_ERROR,
  CHANGE_CONFIGURATIONS_REQUEST_PENDING,
  CHANGE_CONFIGURATIONS_REQUEST_SUCCESS,
  CHANGE_CONFIGURATIONS_REQUEST_ERROR,
} from '../types/configurations'

function* getConfigurationsWatcher() {
  yield takeEvery(GET_CONFIGURATIONS_REQUEST, getConfigurationsWorker)
}

function* getConfigurationWatcher() {
  yield takeEvery(GET_CONFIGURATION_REQUEST, getConfigurationWorker)
}

function* createConfigurationWatcher() {
  yield takeEvery(CREATE_CONFIGURATION_REQUEST, createConfigurationWorker)
}

function* updateConfigurationWatcher() {
  yield takeEvery(UPDATE_CONFIGURATION_REQUEST, updateConfigurationWorker)
}

function* deleteConfigurationWatcher() {
  yield takeEvery(DELETE_CONFIGURATION_REQUEST, deleteConfigurationWorker)
}

function* toggleConfigurationStatusWatcher() {
  yield takeEvery(TOGGLE_CONFIGURATION_STATUS_REQUEST, toggleConfigurationStatusWorker)
}

function* getConfigurationsWorker({ payload: { page, size, sortType, sortOrder, platform, searchKey, isActive } }) {
  try {
    yield put(showLoading())
    yield put({ type: GET_CONFIGURATIONS_REQUEST_PENDING })
    
    const response = yield call(
      ConfigurationsService.getConfigurations,
      page,
      size,
      sortType,
      sortOrder,
      platform,
      searchKey,
      isActive
    )
    
    const { data, total, totalPages } = response.data.data
    
    yield put({
      type: GET_CONFIGURATIONS,
      payload: {
        items: data || [],
        pagination: {
          current: page,
          pageSize: size,
          total: total || 0,
          totalPages: totalPages || 0,
        },
        filters: {
          platform: platform || '',
          searchKey: searchKey || '',
          isActive: isActive || '',
        },
      },
    })
    
    yield put({ type: GET_CONFIGURATIONS_REQUEST_SUCCESS })
    yield put(hideLoading())
  } catch (error) {
    yield put({
      type: GET_CONFIGURATIONS_REQUEST_ERROR,
      payload: error.response?.data?.messages || [{ message: 'Failed to fetch configurations' }],
    })
    yield put(hideLoading())
  }
}

function* getConfigurationWorker({ payload: { id } }) {
  try {
    yield put(showLoading())
    yield put({ type: GET_CONFIGURATIONS_REQUEST_PENDING })
    
    const response = yield call(ConfigurationsService.getConfigurationById, id)
    const configuration = response.data.data
    
    yield put({ type: GET_CONFIGURATION, payload: configuration })
    yield put({ type: GET_CONFIGURATIONS_REQUEST_SUCCESS })
    yield put(hideLoading())
  } catch (error) {
    yield put({
      type: GET_CONFIGURATIONS_REQUEST_ERROR,
      payload: error.response?.data?.messages || [{ message: 'Failed to fetch configuration' }],
    })
    yield put(hideLoading())
  }
}

function* createConfigurationWorker({ payload: { data, navigate } }) {
  try {
    yield put(showLoading())
    yield put({ type: CHANGE_CONFIGURATIONS_REQUEST_PENDING })
    
    const response = yield call(ConfigurationsService.createConfiguration, data)
    const configuration = response.data.data
    
    yield put({ type: CREATE_CONFIGURATION, payload: configuration })
    yield put({ type: CHANGE_CONFIGURATIONS_REQUEST_SUCCESS })
    
    yield put(
      notify({
        title: i18n.t('configuration.messages.created') || 'Configuration created successfully',
        status: 'success',
        dismissAfter: 4000,
        allowHTML: true,
      }),
    )
    
    if (navigate) {
      navigate('/main/configuration')
    }
    
    yield put(hideLoading())
  } catch (error) {
    yield put({
      type: CHANGE_CONFIGURATIONS_REQUEST_ERROR,
      payload: error.response?.data?.messages || [{ message: 'Failed to create configuration' }],
    })
    yield put(hideLoading())
  }
}

function* updateConfigurationWorker({ payload: { id, data, navigate } }) {
  try {
    yield put(showLoading())
    yield put({ type: CHANGE_CONFIGURATIONS_REQUEST_PENDING })
    
    const response = yield call(ConfigurationsService.updateConfiguration, id, data)
    const configuration = response.data.data
    
    yield put({ type: UPDATE_CONFIGURATION, payload: configuration })
    yield put({ type: CHANGE_CONFIGURATIONS_REQUEST_SUCCESS })
    
    yield put(
      notify({
        title: i18n.t('configuration.messages.updated') || 'Configuration updated successfully',
        status: 'success',
        dismissAfter: 4000,
        allowHTML: true,
      }),
    )
    
    if (navigate) {
      navigate('/main/configuration')
    }
    
    yield put(hideLoading())
  } catch (error) {
    yield put({
      type: CHANGE_CONFIGURATIONS_REQUEST_ERROR,
      payload: error.response?.data?.messages || [{ message: 'Failed to update configuration' }],
    })
    yield put(hideLoading())
  }
}

function* deleteConfigurationWorker({ payload: { id } }) {
  try {
    yield put(showLoading())
    yield put({ type: CHANGE_CONFIGURATIONS_REQUEST_PENDING })
    
    yield call(ConfigurationsService.deleteConfiguration, id)
    
    yield put({ type: DELETE_CONFIGURATION, payload: id })
    yield put({ type: CHANGE_CONFIGURATIONS_REQUEST_SUCCESS })
    
    yield put(
      notify({
        title: i18n.t('configuration.messages.deleted') || 'Configuration deleted successfully',
        status: 'success',
        dismissAfter: 4000,
        allowHTML: true,
      }),
    )
    
    yield put(hideLoading())
  } catch (error) {
    yield put({
      type: CHANGE_CONFIGURATIONS_REQUEST_ERROR,
      payload: error.response?.data?.messages || [{ message: 'Failed to delete configuration' }],
    })
    yield put(hideLoading())
  }
}

function* toggleConfigurationStatusWorker({ payload: { id } }) {
  try {
    yield put(showLoading())
    yield put({ type: CHANGE_CONFIGURATIONS_REQUEST_PENDING })
    
    const response = yield call(ConfigurationsService.toggleConfigurationStatus, id)
    const configuration = response.data.data
    
    yield put({ type: TOGGLE_CONFIGURATION_STATUS, payload: configuration })
    yield put({ type: CHANGE_CONFIGURATIONS_REQUEST_SUCCESS })
    
    yield put(
      notify({
        title: i18n.t('configuration.messages.statusToggled') || 'Configuration status updated successfully',
        status: 'success',
        dismissAfter: 4000,
        allowHTML: true,
      }),
    )
    
    yield put(hideLoading())
  } catch (error) {
    yield put({
      type: CHANGE_CONFIGURATIONS_REQUEST_ERROR,
      payload: error.response?.data?.messages || [{ message: 'Failed to toggle configuration status' }],
    })
    yield put(hideLoading())
  }
}

export default function* configurations() {
  yield all([
    fork(getConfigurationsWatcher),
    fork(getConfigurationWatcher),
    fork(createConfigurationWatcher),
    fork(updateConfigurationWatcher),
    fork(deleteConfigurationWatcher),
    fork(toggleConfigurationStatusWatcher),
  ])
}
